import { defineStore } from 'pinia';
import { ref } from 'vue';

import { TaxeService } from '@/services/taxe.services';
import type {
  IBodyTax,
  IBodyTaxUpdate,
  ITaxeResponse,
} from '@/types/taxe.types';
import type { AxiosError } from 'axios';

/**
 * Store pour gérer les  users et leurs actions associées
 */
export const useTaxeStore = defineStore('taxe', () => {
  /**
   * Crée une nouvelle taxe via le service distant.
   *
   * Cette fonction envoie les données d'une taxe au backend pour
   * l'enregistrement dans la base de données.
   *
   * @async
   * @function createTax
   * @param {IBodyTax} data - Données de la taxe à créer.
   * @returns {Promise<any>} Réponse de l'API contenant les informations de la taxe créée.
   * @throws {AxiosError} Lève une erreur si la requête échoue ou si le service renvoie une erreur.
   */
  const createTaxe = async (data: IBodyTax): Promise<any> => {
    try {
      const response = await TaxeService.createTaxe(data);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * État réactif contenant la liste des taxes récupérées depuis l’API.
   *
   * Ce ref stocke la réponse typée de l’API après l’appel à `getTaxe`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<ITaxeResponse | null>}
   */
  const datagetTaxe = ref<ITaxeResponse | null>(null);

  /**
   * Récupère la liste des taxes depuis le service distant.
   *
   * Cette fonction effectue une requête HTTP via le `TaxeService` service
   * pour récupérer les taxes disponibles, puis met à jour
   * l’état réactif `datagetTaxe` avec les données renvoyées.
   *
   * @async
   * @function getTaxe
   * @returns {Promise<ITaxeResponse>} Promesse résolue contenant les données des taxes récupérées.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getTaxe = async (): Promise<ITaxeResponse> => {
    try {
      const response = await TaxeService.getTaxe();
      datagetTaxe.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * État réactif contenant les informations d’une taxe récupérée depuis l’API.
   *
   * Ce `ref` stocke la réponse typée de l’API après l’appel à `getTaxeById`.
   * Il est mis à jour automatiquement lorsque les données sont chargées
   * depuis le service distant.
   *
   * @type {Ref<ITax | null>}
   */
  const datagetTaxeById = ref<any>(null);

  /**
   * Récupère une taxe spécifique depuis le service distant par son identifiant.
   *
   * Cette fonction effectue une requête HTTP via `TaxeService`
   * pour obtenir les informations de la taxe, puis met à jour
   * l’état réactif `datagetTaxeById` avec les données reçues.
   *
   * @async
   * @function getTaxeById
   * @returns {Promise<ITax>} Promesse résolue contenant les données de la taxe récupérée.
   * @throws {AxiosError} Lève une erreur si la récupération échoue.
   */
  const getTaxeById = async (): Promise<any> => {
    try {
      const response = await TaxeService.getTaxeById();
      datagetTaxeById.value = response.data;
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Met à jour une taxe spécifique sur le service distant.
   *
   * Cette fonction effectue une requête HTTP via `TaxeService`
   * pour modifier les informations d’une taxe existante,
   * puis renvoie les données mises à jour.
   *
   * @async
   * @function updateTaxById
   * @param {Partial<IBodyTaxUpdate>} taxeData - Données partielles de la taxe à mettre à jour.
   * @returns {Promise<ITax>} Promesse résolue contenant les données mises à jour de la taxe.
   * @throws {AxiosError} Lève une erreur si la mise à jour échoue.
   */
  const updateTaxById = async (
    taxeData: Partial<IBodyTaxUpdate>,
  ): Promise<any> => {
    try {
      const response = await TaxeService.updateTaxById(taxeData);
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  /**
   * Supprime une taxe spécifique via le service distant.
   *
   * Cette fonction envoie une requête HTTP à l’aide de `TaxeService`
   * pour retirer une taxe existante,
   * puis renvoie la réponse du serveur en cas de succès.
   *
   * @async
   * @function deleteTaxById
   * @returns {Promise<any>} Promesse résolue contenant la réponse du serveur après suppression.
   * @throws {AxiosError} Lève une erreur si la suppression échoue.
   */
  const deleteTaxById = async (): Promise<any> => {
    try {
      const response = await TaxeService.deleteTaxById();
      return response;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  };

  return {
    createTaxe,
    getTaxe,
    datagetTaxe,
    getTaxeById,
    datagetTaxeById,
    updateTaxById,
    deleteTaxById,
  };
});
